import { Keyboard, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import type { CompanyContext, UserProfile } from "@/shared/api/api-client";
import {
  OPEN_MESTRA_EVENT,
  SEQUENCE_TIMEOUT_MS,
  SHORTCUTS,
  inDisplayOrder,
  SHORTCUT_GROUPS,
  isTypingTarget,
  onShortcutsEnabledChange,
  sequenceFor,
  setShortcutsEnabled,
  shortcutsEnabled,
  startsSequence,
  type Shortcut
} from "@/shared/keyboard/shortcuts";
import { ShortcutKeys } from "@/shared/keyboard/ShortcutKeys";
import { activeCompanies } from "@/shared/utils/companies";
import { hasRoleAtLeast } from "@/shared/utils/roles";
import { carriedSearch, navigationItems } from "@/shell/navigation";

type Props = {
  company: CompanyContext | null;
  profile: UserProfile;
  toggleSidebar: () => void;
};

/** A empresa que os atalhos de tela de empresa usam: a aberta na URL e, fora
 *  de uma (Início, Configurações, Administração), a ÚLTIMA ABERTA que a pessoa
 *  ainda acessa — a mesma regra do painel da Home. Sem isso, G M fora de uma
 *  empresa simplesmente não fazia nada. */
export function shortcutCompany(company: CompanyContext | null, profile: UserProfile): CompanyContext | null {
  if (company) return company;
  let recent: unknown = [];
  try {
    recent = JSON.parse(window.localStorage.getItem("reputation_recent_companies") ?? "[]");
  } catch {
    recent = [];
  }
  const active = activeCompanies(profile.companies);
  const ids = Array.isArray(recent) ? recent : [];
  return ids.map(id => active.find(item => item.id === id)).find(item => item !== undefined) ?? null;
}

/** O destino de um atalho de navegação — ou nulo se o menu não mostraria
 *  aquela tela (sem empresa, sem papel, sem CENA). */
export function destinationFor(item: Shortcut, company: CompanyContext | null, profile: UserProfile): string | null {
  const nav = navigationItems.find(entry => entry.label === item.nav);
  if (!nav) return null;
  if (nav.requiresRole && !hasRoleAtLeast(profile, nav.requiresRole)) return null;
  if (nav.scope === "company") {
    if (!company) return null;
    if (nav.requiresCena && company.displays_cena === false) return null;
    return nav.href(company.id);
  }
  return nav.href();
}

/**
 * O ouvinte global dos atalhos de teclado e a lista que aparece no ?
 * (2026-10-16). Respeita a preferência "Ativar atalhos de teclado": desligada,
 * nenhuma tecla faz nada.
 */
export function KeyboardShortcuts({ company, profile, toggleSidebar }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [enabled, setEnabled] = useState(shortcutsEnabled);
  const [helpOpen, setHelpOpen] = useState(false);
  const pending = useRef<{ key: string; at: number } | null>(null);
  // O ouvinte é registrado uma vez; o que muda entre renders vem por ref.
  const latest = useRef({ company, profile, toggleSidebar, navigate, helpOpen, location });
  latest.current = { company, profile, toggleSidebar, navigate, helpOpen, location };

  useEffect(() => onShortcutsEnabledChange(() => setEnabled(shortcutsEnabled())), []);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      const now = latest.current;
      // ⌘B / Ctrl+B: vale de qualquer lugar, até dentro de campo.
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "b") {
        event.preventDefault();
        now.toggleSidebar();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      if (event.key === "?") {
        event.preventDefault();
        setHelpOpen(open => !open);
        pending.current = null;
        return;
      }
      if (event.key === "Escape" && now.helpOpen) {
        setHelpOpen(false);
        return;
      }

      const first = pending.current;
      if (first && Date.now() - first.at <= SEQUENCE_TIMEOUT_MS) {
        pending.current = null;
        const item = sequenceFor(first.key, event.key);
        if (item?.action === "mestra") {
          event.preventDefault();
          setHelpOpen(false);
          window.dispatchEvent(new Event(OPEN_MESTRA_EVENT));
          return;
        }
        if (item) {
          const target = destinationFor(item, shortcutCompany(now.company, now.profile), now.profile);
          if (target) {
            event.preventDefault();
            setHelpOpen(false);
            // A mesma regra do link do menu: entre Menções e Justificativas
            // da mesma empresa, o filtro de upload vai junto (2026-10-22).
            now.navigate(target + carriedSearch(now.location.pathname, now.location.search, target));
          }
        }
        return;
      }
      pending.current = startsSequence(event.key) ? { key: event.key, at: Date.now() } : null;
    };
    // Na CAPTURA: telas com componentes que interceptam o teclado (listas,
    // editores) não engolem o atalho antes de ele chegar aqui.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [enabled]);

  if (!helpOpen) return null;
  return (
    <ShortcutsHelp
      company={company}
      profile={profile}
      enabled={enabled}
      onToggle={next => setShortcutsEnabled(next)}
      onClose={() => setHelpOpen(false)}
    />
  );
}

function ShortcutsHelp({
  company,
  profile,
  enabled,
  onToggle,
  onClose
}: {
  company: CompanyContext | null;
  profile: UserProfile;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  onClose: () => void;
}) {
  // Só lista o que funciona AGORA: tela de empresa sem empresa aberta, ou sem
  // o papel, não aparece.
  const target = shortcutCompany(company, profile);
  const visible = SHORTCUTS.filter(item => !item.nav || destinationFor(item, target, profile) !== null);
  return createPortal(
    <>
      <div className="shortcuts-scrim" onClick={onClose} />
      <div className="shortcuts-dialog" role="dialog" aria-modal="true" aria-label="Atalhos de teclado">
        <header className="shortcuts-head">
          <span className="shortcuts-icon"><Keyboard size={18} aria-hidden="true" /></span>
          <div>
            <h2>Atalhos de teclado</h2>
            <p>Pressione ? a qualquer momento para ver esta lista.</p>
          </div>
          <button type="button" className="shortcuts-close" onClick={onClose} aria-label="Fechar">
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div className="shortcuts-body">
          {SHORTCUT_GROUPS.map(group => {
            const items = inDisplayOrder(visible.filter(item => item.group === group.key));
            if (!items.length) return null;
            return (
              <section key={group.key} className="shortcuts-group">
                <h3>{group.label}</h3>
                {group.hint && <p className="shortcuts-hint">{group.hint}</p>}
                <ul>
                  {items.map(item => (
                    <li key={item.id}>
                      <span>{item.label}</span>
                      <ShortcutKeys item={item} />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
          {!target ? (
            <p className="shortcuts-hint">Abra uma empresa para ver também os atalhos das telas dela.</p>
          ) : !company ? (
            <p className="shortcuts-hint">Fora de uma empresa, as telas de empresa abrem em {target.name}, a última aberta.</p>
          ) : null}
        </div>
        <footer className="shortcuts-foot">
          <label className="shortcuts-toggle">
            <input type="checkbox" checked={enabled} onChange={event => onToggle(event.target.checked)} />
            <span>Ativar atalhos de teclado</span>
          </label>
        </footer>
      </div>
    </>,
    document.body
  );
}
